import { useCallback, useEffect, useRef, useState } from 'react';

import { buildAttachmentMarker, parseAttachmentMarker } from '@shiftagent/shared';
import { useApiClient } from '../providers/auth-provider';
import { mapEventsForReplay, useStreamMessages } from './use-stream-messages';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string | undefined;
  status?: 'sending' | 'sent' | 'error' | undefined;
  streamEvents?: Record<string, unknown>[] | undefined;
  error?: string | undefined;
}

export interface UseConversationMessagesOptions {
  onStreamComplete?: ((conversationId: string) => void) | undefined;
}

const STREAMING_MSG_ID = 'asst_streaming';

/** System trigger messages contain credentials and must never be shown in the UI. */
function isSystemTriggerMessage(content: string): boolean {
  return content.startsWith('[SYSTEM_TRIGGER:');
}

/** Polling interval for extended session mode (ms) */
const EXTENDED_SESSION_POLL_INTERVAL = 5_000;
/** Safety cap: stop polling after 60 minutes */
const EXTENDED_SESSION_MAX_DURATION = 60 * 60 * 1_000;

export function useConversationMessages(
  conversationId: string | null,
  { onStreamComplete }: UseConversationMessagesOptions = {},
) {
  const apiClient = useApiClient();
  const streamMessages = useStreamMessages();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const [isExtendedSession, setIsExtendedSession] = useState(false);
  const [error, setError] = useState<string | undefined>();
  // F4: Guard against race condition where useEffect fires after sendMessage cleanup
  const streamFinalizedRef = useRef(false);
  // Extended session polling refs
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingStartRef = useRef<number>(0);

  /** Stop any active extended session polling */
  const stopPolling = useCallback(() => {
    if (pollingRef.current !== null) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  /** Start extended session polling mode — fetches incremental sdk_events on every tick */
  const startExtendedSession = useCallback(
    (convId: string) => {
      stopPolling();
      setIsExtendedSession(true);
      setIsAgentTyping(true);
      pollingStartRef.current = Date.now();

      const poll = async () => {
        // Safety cap — stop polling after max duration
        if (Date.now() - pollingStartRef.current > EXTENDED_SESSION_MAX_DURATION) {
          stopPolling();
          setIsExtendedSession(false);
          setIsAgentTyping(false);
          return;
        }

        try {
          // Fetch conversation status AND latest messages in parallel
          const [conversation, messagesRes] = await Promise.all([
            apiClient.getConversation(convId),
            apiClient.listMessages(convId, { event_types: 'stream_event,assistant,user,result' }),
          ]);

          const isComplete = conversation.processing_status === 'idle';

          // Update messages with latest sdk_events from DB (incremental rendering)
          const allMessages: ChatMessage[] = messagesRes.data
            .filter((m) => !isSystemTriggerMessage(m.content))
            .map((m) => {
              const streamEvents = mapEventsForReplay(m.sdk_events ?? []);
              const hasStreamEvents = streamEvents.length > 0;
              return {
                id: m.id ?? String(Math.random()),
                role: (m.role === 'human' ? 'user' : 'assistant') as 'user' | 'assistant',
                content: hasStreamEvents && m.role !== 'human' ? '' : m.content,
                timestamp: m.created_at,
                streamEvents: hasStreamEvents ? streamEvents : undefined,
              };
            });

          if (isComplete) {
            // Agent finished — show final messages
            stopPolling();
            setMessages(allMessages);
            setIsExtendedSession(false);
            setIsAgentTyping(false);
            onStreamComplete?.(convId);
          } else {
            // Still processing — show incremental progress from DB
            // The typing indicator in ChatPanel handles the "extended session" messaging
            setMessages(allMessages);
          }
        } catch {
          // Network error during poll — continue polling (transient)
        }
      };

      // Run first poll immediately, then on interval
      void poll();
      pollingRef.current = setInterval(poll, EXTENDED_SESSION_POLL_INTERVAL);
    },
    [apiClient, onStreamComplete, stopPolling],
  );

  // Load messages on conversation change — also detect if conversation is currently processing
  useEffect(() => {
    if (!conversationId) return;
    setMessages([]);
    setStatus('loading');
    setIsExtendedSession(false);
    stopPolling();

    const loadConversation = async () => {
      try {
        // Fetch messages
        const res = await apiClient.listMessages(conversationId, { event_types: 'stream_event,assistant,user,result' });
        const loadedMessages = res.data
          .filter((m) => !isSystemTriggerMessage(m.content))
          .map((m) => {
            // Map stored SDK events through replay mapper for StreamRenderer-compatible events
            const streamEvents = mapEventsForReplay(m.sdk_events ?? []);
            const hasStreamEvents = streamEvents.length > 0;
            return {
              id: m.id ?? String(Math.random()),
              role: (m.role === 'human' ? 'user' : 'assistant') as 'user' | 'assistant',
              // When streamEvents exist, StreamRenderer handles ALL rendering (text + tools interleaved).
              // Pass empty content to prevent MessageBubble from rendering a duplicate text blob.
              content: hasStreamEvents && m.role !== 'human' ? '' : m.content,
              timestamp: m.created_at,
              streamEvents: hasStreamEvents ? streamEvents : undefined,
            };
          });
        setMessages(loadedMessages);
        setStatus('success');

        // Check if conversation is currently processing (cross-browser resume)
        // startExtendedSession runs the first poll immediately which fetches
        // incremental sdk_events and appends the status indicator.
        try {
          const conversation = await apiClient.getConversation(conversationId);
          if (conversation.processing_status === 'processing') {
            startExtendedSession(conversationId);
          }
        } catch {
          // Failed to check processing status — non-critical
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load messages');
        setStatus('error');
      }
    };

    void loadConversation();
  }, [apiClient, conversationId, startExtendedSession, stopPolling]);

  // Reactively build an in-progress assistant message from streaming events
  useEffect(() => {
    // F4: Skip if sendMessage already finalized this stream
    if (streamFinalizedRef.current) return;
    if (streamMessages.status !== 'streaming' || streamMessages.events.length === 0) return;
    // Include ALL events (including content_block_delta) in streamEvents so StreamRenderer
    // can interleave text and tool cards in chronological order
    const evts: Record<string, unknown>[] = [];
    for (const e of streamMessages.events) {
      if (e.type !== 'message_stop') evts.push(e);
    }
    if (evts.length === 0) return;
    setMessages((prev) => [
      ...prev.filter((m) => m.id !== STREAMING_MSG_ID),
      { id: STREAMING_MSG_ID, role: 'assistant' as const, content: '', streamEvents: evts, timestamp: new Date().toISOString() },
    ]);
  }, [streamMessages.status, streamMessages.events]);

  // Abort any in-flight stream and reset finalized flag when conversationId changes
  const prevConversationIdRef = useRef(conversationId);
  useEffect(() => {
    if (prevConversationIdRef.current !== conversationId) {
      streamMessages.abort();
      setIsAgentTyping(false);
      setIsExtendedSession(false);
      stopPolling();
      streamFinalizedRef.current = false;
    }
    prevConversationIdRef.current = conversationId;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- streamMessages.abort is stable (useCallback with [])
  }, [conversationId, streamMessages.abort, stopPolling]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { stopPolling(); };
  }, [stopPolling]);

  const sendMessage = useCallback(
    async (content: string, files?: File[]) => {
      if (!conversationId) return;
      streamFinalizedRef.current = false;
      const optimisticId = `opt_${Date.now()}`;
      const isSystemTrigger = isSystemTriggerMessage(content);
      const optimisticContent = files && files.length > 0
        ? content + buildAttachmentMarker(files.map((f) => f.name))
        : content;
      // Don't show system trigger messages as user bubbles
      if (!isSystemTrigger) {
        setMessages((prev) => [
          ...prev,
          { id: optimisticId, role: 'user', content: optimisticContent, status: 'sending', timestamp: new Date().toISOString() },
        ]);
      }
      setIsAgentTyping(true);
      setIsExtendedSession(false);
      stopPolling();

      // Upload files and collect their IDs
      let fileIds: string[] | undefined;
      if (files && files.length > 0) {
        try {
          const uploaded = await Promise.all(
            files.map((f) => apiClient.uploadFile(conversationId, f)),
          );
          fileIds = uploaded.map((u) => u.id);
        } catch {
          // File upload failed — mark the message as error so user can retry
          setMessages((prev) =>
            prev.map((m) => m.id === optimisticId ? { ...m, status: 'error' as const, error: 'Failed to upload attachments' } : m),
          );
          setIsAgentTyping(false);
          return;
        }
      }

      const result = await streamMessages.sendMessage(conversationId, content, {
        ...(fileIds ? { file_ids: fileIds } : {}),
        stream_events: ['chat_tools'],
      });

      // F4: Mark stream as finalized so the useEffect won't inject stale placeholders
      streamFinalizedRef.current = true;

      // Stream was interrupted (CF container cycle) — enter extended session mode
      if (result.status === 'interrupted') {
        // Keep isAgentTyping = true (input stays locked)
        // Replace streaming placeholder with partial events + status indicator
        const streamEvts: Record<string, unknown>[] = [];
        for (const event of result.events) {
          if (event.type !== 'message_stop') {
            streamEvts.push(event);
          }
        }

        setMessages((prev) => {
          const mapped = prev.map((m) => {
            if (m.id === STREAMING_MSG_ID) {
              return { ...m, id: `asst_${Date.now()}`, content: '', streamEvents: streamEvts.length > 0 ? streamEvts : undefined };
            }
            if (m.id === optimisticId) return { ...m, status: 'sent' as const };
            return m;
          });
          // If no streaming placeholder existed, append the partial assistant message
          if (!prev.some((m) => m.id === STREAMING_MSG_ID) && streamEvts.length > 0) {
            mapped.push({ id: `asst_${Date.now()}`, role: 'assistant' as const, content: '', streamEvents: streamEvts });
          }
          return mapped;
        });

        startExtendedSession(conversationId);
        return;
      }

      if (result.status === 'error') {
        setMessages((prev) =>
          prev
            .filter((m) => m.id !== STREAMING_MSG_ID)
            .map((m) => m.id === optimisticId ? { ...m, status: 'error' as const, error: result.error } : m),
        );
        setIsAgentTyping(false);
        return;
      }

      // Include ALL events (including content_block_delta) in streamEvents so StreamRenderer
      // interleaves text and tool cards chronologically
      const streamEvts: Record<string, unknown>[] = [];
      for (const event of result.events) {
        if (event.type !== 'message_stop') {
          streamEvts.push(event);
        }
      }

      setMessages((prev) => {
        const hasPlaceholder = prev.some((m) => m.id === STREAMING_MSG_ID);
        const mapped = prev.map((m) => {
          if (m.id === STREAMING_MSG_ID) {
            return { ...m, id: `asst_${Date.now()}`, content: '', timestamp: m.timestamp ?? new Date().toISOString(), streamEvents: streamEvts.length > 0 ? streamEvts : undefined };
          }
          if (m.id === optimisticId) return { ...m, status: 'sent' as const };
          return m;
        });
        // If no streaming placeholder existed (e.g. fast responses), append the assistant message
        if (!hasPlaceholder && streamEvts.length > 0) {
          mapped.push({ id: `asst_${Date.now()}`, role: 'assistant' as const, content: '', timestamp: new Date().toISOString(), streamEvents: streamEvts });
        }
        return mapped;
      });
      setIsAgentTyping(false);
      onStreamComplete?.(conversationId);
    },
    [apiClient, conversationId, onStreamComplete, streamMessages.sendMessage, startExtendedSession, stopPolling],
  );

  const retryLastMessage = useCallback(async () => {
    // Find last failed user message
    const lastUser = [...messages].reverse().find((m) => m.role === 'user' && m.status === 'error');
    if (!lastUser) return;
    // Remove the failed message first to prevent duplicates when sendMessage adds new optimistic message
    setMessages((prev) => prev.filter((m) => m.id !== lastUser.id));
    // Strip attachment marker from stored content so sendMessage doesn't double-append it
    const { text: rawContent } = parseAttachmentMarker(lastUser.content);
    await sendMessage(rawContent);
  }, [messages, sendMessage]);

  /**
   * Re-fetch messages from the API. Used after plan approval triggers new agent
   * responses that aren't visible through the existing stream.
   */
  const refetchMessages = useCallback(async () => {
    if (!conversationId) return;
    try {
      const res = await apiClient.listMessages(conversationId, { event_types: 'stream_event,assistant,user,result' });
      setMessages(
        res.data
          .filter((m) => !isSystemTriggerMessage(m.content))
          .map((m) => {
            const streamEvents = mapEventsForReplay(m.sdk_events ?? []);
            const hasStreamEvents = streamEvents.length > 0;
            return {
              id: m.id ?? String(Math.random()),
              role: (m.role === 'human' ? 'user' : 'assistant') as 'user' | 'assistant',
              content: hasStreamEvents && m.role !== 'human' ? '' : m.content,
              timestamp: m.created_at,
              streamEvents: hasStreamEvents ? streamEvents : undefined,
            };
          }),
      );
    } catch { /* best-effort refresh */ }
  }, [apiClient, conversationId]);

  return { messages, status, error, isAgentTyping, isExtendedSession, sendMessage, retryLastMessage, refetchMessages };
}
